/* Copyright © 2026 YOUR GROWTH 365 (Jonas Brössel) — Proprietary. See LICENSE in repo root. */
/* =============================================================================
   CORE / SCHEDULE-SHIFTS — Single-Source der Dienstplan-Codes (S262 Cut A)
   =============================================================================
   Spec: docs/s262-dienstplan-pre-cut.md § 2. Alles hier ist rein (kein DOM,
   kein window) und spiegelt die SQL-CHECKs aus phase_193:

     Schicht-Codes      early 12–16 · late 16–22 · full 12–22
     Verfügbarkeit      early · late · either (12–16 ODER 16–22) · full · none

   Die Matrix „wer erscheint in welcher Spalte" (Cut B, Manager-Raster):

     Verfügbarkeit | 12–16 | 16–22 | 12–22
     early         |  ✅   |   –   |   –
     late          |   –   |  ✅   |   –
     either        |  ✅   |  ✅   |   –
     full          |  ✅   |  ✅   |  ✅
     none / kein   |   –   |   –   |   –

   Jonas-Entscheid 3: kein Eintrag = keine Stunden. Es gibt KEINEN Default
   „verfügbar" — `columnsForChoice(undefined)` ist deshalb leer, nicht `full`.

   Datums-Helfer arbeiten auf reinen 'YYYY-MM-DD'-Strings in UTC-Arithmetik —
   `week_start` ist ein Datum ohne Zeitzone, ein lokales `new Date('2026-09-21')`
   würde je nach Browser-Zone einen Tag verrutschen.
============================================================================ */

export const SHIFT_CODES = Object.freeze(['early', 'late', 'full']);
export const SHIFT_LABELS = Object.freeze({ early: '12–16', late: '16–22', full: '12–22' });

/** Reihenfolge = die fünf Pills auf der Mitarbeiter-Seite (Spec § 6.1). */
export const CHOICE_CODES = Object.freeze(['early', 'late', 'either', 'full', 'none']);
export const CHOICE_LABELS = Object.freeze({
  early:  '12–16',
  late:   '16–22',
  either: '12–16 oder 16–22',
  full:   '12–22',
  none:   'nicht verfügbar',
});

/** Verfügbarkeit → Spalten, in denen die Person als „verfügbar" erscheint. */
export const AVAILABILITY_MATRIX = Object.freeze({
  early:  Object.freeze(['early']),
  late:   Object.freeze(['late']),
  either: Object.freeze(['early', 'late']),
  full:   Object.freeze(['early', 'late', 'full']),
  none:   Object.freeze([]),
});

/** Defaults von `settings.schedule` (phase_193 setzt sie per jsonb-Merge). */
export const SCHEDULE_SETTINGS_DEFAULTS = Object.freeze({
  enabled: false,
  requestDow: 4,  requestTime:  '10:00',
  reminderDow: 6, reminderTime: '10:00',
  deadlineDow: 7, deadlineTime: '12:00',
});

export const WEEKDAY_SHORT = Object.freeze(['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']);
export const WEEKDAY_LONG  = Object.freeze(['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag']);

export function isValidChoice(code) {
  return typeof code === 'string' && CHOICE_CODES.indexOf(code) !== -1;
}

export function isValidShift(code) {
  return typeof code === 'string' && SHIFT_CODES.indexOf(code) !== -1;
}

/** Spalten für eine Verfügbarkeit; unbekannt/leer → [] (kein Eintrag = keine Stunden). */
export function columnsForChoice(choice) {
  return AVAILABILITY_MATRIX[choice] || AVAILABILITY_MATRIX.none;
}

/** Server-Guard-Spiegel (Cut B): deckt die Verfügbarkeit die Schicht? */
export function choiceCoversShift(choice, shift) {
  return isValidShift(shift) && columnsForChoice(choice).indexOf(shift) !== -1;
}

/* --------------------------------------------------------------------------
   Datum (reine 'YYYY-MM-DD'-Strings, UTC-Arithmetik)
   -------------------------------------------------------------------------- */

export function parseIsoDate(s) {
  if (typeof s !== 'string') return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]), mo = Number(m[2]), d = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
  return { y, m: mo, d, date: dt };
}

function _iso(dt) {
  return dt.getUTCFullYear() + '-'
    + String(dt.getUTCMonth() + 1).padStart(2, '0') + '-'
    + String(dt.getUTCDate()).padStart(2, '0');
}

export function isoDateAdd(s, days) {
  const p = parseIsoDate(s);
  if (!p) return null;
  return _iso(new Date(p.date.getTime() + (Number(days) || 0) * 86400000));
}

/** ISO-Wochentag 1 (Mo) … 7 (So); null bei ungültigem Datum. */
export function isoDow(s) {
  const p = parseIsoDate(s);
  if (!p) return null;
  return p.date.getUTCDay() || 7;
}

/** Die sieben Tage einer Woche ab `weekStart` (muss ein Montag sein, sonst []). */
export function weekDays(weekStart) {
  if (isoDow(weekStart) !== 1) return [];
  const out = [];
  for (let i = 0; i < 7; i++) out.push(isoDateAdd(weekStart, i));
  return out;
}

/** ISO-Kalenderwoche (Spiegel von `extract(week from date)` und Edge isoWeekNo). */
export function isoWeekNumber(s) {
  const p = parseIsoDate(s);
  if (!p) return 0;
  const d = new Date(p.date.getTime());
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const y0 = Date.UTC(d.getUTCFullYear(), 0, 1);
  return Math.ceil(((d.getTime() - y0) / 86400000 + 1) / 7);
}

function _ddmm(p) {
  return String(p.d).padStart(2, '0') + '.' + String(p.m).padStart(2, '0') + '.';
}

/** 'Mo 21.09.' */
export function formatDayShort(s) {
  const p = parseIsoDate(s);
  if (!p) return '';
  return WEEKDAY_SHORT[(p.date.getUTCDay() || 7) - 1] + ' ' + _ddmm(p);
}

/** 'Montag, 21.09.' */
export function formatDayLong(s) {
  const p = parseIsoDate(s);
  if (!p) return '';
  return WEEKDAY_LONG[(p.date.getUTCDay() || 7) - 1] + ', ' + _ddmm(p);
}

/** 'Mo 21.09. – So 27.09.2026' (Spiegel von Edge weekRangeDe). */
export function formatWeekRange(weekStart) {
  const a = parseIsoDate(weekStart);
  if (!a) return '';
  const b = parseIsoDate(isoDateAdd(weekStart, 6));
  return 'Mo ' + _ddmm(a) + ' – So ' + _ddmm(b) + b.y;
}

/**
 * Deadline als Wanduhrzeit des Restaurants: 'So, 27.09., 12:00 Uhr'
 * (Spiegel von Edge deadlineDe). `iso` ist ein timestamptz-String.
 */
export function formatDeadline(iso, tz) {
  if (typeof iso !== 'string' || !iso) return '';
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return '';
  try {
    const parts = new Intl.DateTimeFormat('de-DE', {
      timeZone: tz || 'Europe/Berlin',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(dt);
    const g = (t) => { const x = parts.find(q => q.type === t); return x ? x.value : ''; };
    const y = Number(g('year')), mo = Number(g('month')), d = Number(g('day'));
    const dow = new Date(Date.UTC(y, mo - 1, d)).getUTCDay() || 7;
    let hh = g('hour');
    if (hh === '24') hh = '00';
    return WEEKDAY_SHORT[dow - 1] + ', ' + String(d).padStart(2, '0') + '.'
      + String(mo).padStart(2, '0') + '., ' + hh + ':' + g('minute') + ' Uhr';
  } catch (_) {
    return iso;
  }
}

/* --------------------------------------------------------------------------
   Auswahl-Zustand (Mitarbeiter-Seite)
   -------------------------------------------------------------------------- */

/** Tage ohne gültige Wahl — Absenden erst bei 7/7. */
export function missingDays(days, choices) {
  const c = choices || {};
  return (Array.isArray(days) ? days : []).filter(d => !isValidChoice(c[d]));
}

export function isComplete(days, choices) {
  return Array.isArray(days) && days.length === 7 && missingDays(days, choices).length === 0;
}

/**
 * `choices` auf genau die sieben Tage der Woche zuschneiden (das Objekt, das
 * `schedule_submit_availability` erwartet). null, wenn nicht vollständig.
 */
export function buildSubmitPayload(days, choices) {
  if (!isComplete(days, choices)) return null;
  const out = {};
  for (const d of days) out[d] = choices[d];
  return out;
}

/** Aus der RPC-Antwort (`days: [{day, choice}]`) das Auswahl-Objekt bauen. */
export function choicesFromDays(days) {
  const out = {};
  for (const row of (Array.isArray(days) ? days : [])) {
    if (row && typeof row.day === 'string' && isValidChoice(row.choice)) out[row.day] = row.choice;
  }
  return out;
}

/* --------------------------------------------------------------------------
   Settings-Slot
   -------------------------------------------------------------------------- */

const _TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Slot über die Defaults legen, Uhrzeiten/Wochentage klemmen (Spiegel von
 *  SQL `_schedule_settings`). */
export function normalizeScheduleSettings(slot) {
  const s = (slot && typeof slot === 'object') ? slot : {};
  const out = { ...SCHEDULE_SETTINGS_DEFAULTS };
  out.enabled = s.enabled === true;
  for (const k of ['requestDow', 'reminderDow', 'deadlineDow']) {
    const n = parseInt(s[k], 10);
    if (Number.isFinite(n) && n >= 1 && n <= 7) out[k] = n;
  }
  for (const k of ['requestTime', 'reminderTime', 'deadlineTime']) {
    if (typeof s[k] === 'string' && _TIME_RE.test(s[k])) out[k] = s[k];
  }
  return out;
}
